<template>
  <div
    class="ub"
    data-testid="checkout-page"
  >
    <apollo-error-alert
      v-if="fetchError"
      :error="fetchError"
    />

    <!-- Loading -->
    <div
      v-else-if="initialLoading"
      class="ub__shell"
      data-testid="checkout-loading"
    >
      <div class="ub__main">
        <v-skeleton-loader
          type="card"
          min-height="280"
          class="ub__skel"
        />
        <v-skeleton-loader
          type="card"
          min-height="200"
          class="ub__skel"
        />
      </div>
      <aside class="ub__side">
        <v-skeleton-loader
          type="card"
          min-height="400"
          class="ub__skel"
        />
      </aside>
    </div>

    <!-- Order confirmed — payment component handles navigation, suppress empty flash -->
    <div
      v-else-if="order && order.confirmed"
      class="ub__shell"
    />

    <!-- Empty cart (not for Buy Now — handled separately) -->
    <EmptyCartState
      v-else-if="!isBuyNowFlow && (!order || !order.orderLines?.totalCount)"
    />

    <!-- Content -->
    <div
      v-else-if="order"
      class="ub__shell"
      data-testid="checkout-content"
    >
      <!-- ═══════ LEFT COLUMN: Payment ═══════ -->
      <div class="ub__main">
        <!-- Payment method card -->
        <section
          class="ub__card"
          data-testid="payment-method-card"
        >
          <div class="ub__card-head">
            <h2 class="ub__h2">
              {{ $t("checkout.paymentMethod") }}
            </h2>
            <div
              v-if="!isBuyNowFlow && (isAdmin || isChecker)"
              class="ub__pill-toggle"
            >
              <button
                :class="['ub__pill', { 'ub__pill--on': !isAdminOrder }]"
                @click="isAdminOrder = false"
              >
                {{ $t('checkout.standardOrder') }}
              </button>
              <button
                :class="['ub__pill', { 'ub__pill--on': isAdminOrder }]"
                @click="isAdminOrder = true"
              >
                {{ $t('checkout.adminOrder') }}
              </button>
            </div>
          </div>

          <!-- Zero-cost order -->
          <div
            v-if="order.totalPrice == 0"
            class="ub__form-area"
          >
            <no-cost-order container-classes="" />
          </div>

          <!-- Paid order — method selector -->
          <div
            v-else
            class="ub__methods"
          >
            <v-radio-group
              v-model="paymentOption"
              class="ub__rg"
              hide-details
            >
              <!-- M-Pesa -->
              <PaymentMethodRadio
                v-model="paymentOption"
                :selected="paymentOption == paymentTypes.mpesa"
                :label="$t('checkout.mpesaOrder')"
                :radio-value="paymentTypes.mpesa"
              >
                <template #content>
                  <ekitabu-image
                    contain
                    max-width="80"
                    max-height="26"
                    :src="mpesaLogo"
                  />
                </template>
              </PaymentMethodRadio>

              <!-- MTN MoMo -->
              <PaymentMethodRadio
                v-if="!isBuyNowFlow && isFeatureEnabled('momoMtnPayment')"
                v-model="paymentOption"
                :selected="paymentOption == paymentTypes.mtn"
                :label="$t('checkout.mtnOrder')"
                :radio-value="paymentTypes.mtn"
              >
                <template #content>
                  <ekitabu-image
                    contain
                    max-height="26"
                    max-width="80"
                    :src="mtnLogo"
                  />
                </template>
              </PaymentMethodRadio>

              <!-- Cash (admin) -->
              <PaymentMethodRadio
                v-if="showAdminPaymentOptions"
                v-model="paymentOption"
                :selected="paymentOption == paymentTypes.cashPayment"
                :label="$t('checkout.cashOrder')"
                :radio-value="paymentTypes.cashPayment"
              >
                <template #content>
                  <svg
                    class="ub__pm-svg"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    viewBox="0 0 24 24"
                  ><rect
                    x="2"
                    y="6"
                    width="20"
                    height="12"
                    rx="2"
                  /><circle
                    cx="12"
                    cy="12"
                    r="3"
                  /></svg>
                </template>
              </PaymentMethodRadio>

              <!-- Paybill (admin) -->
              <PaymentMethodRadio
                v-if="showAdminPaymentOptions"
                v-model="paymentOption"
                :selected="paymentOption == paymentTypes.paybill"
                :label="$t('checkout.paybillOrder')"
                :radio-value="paymentTypes.paybill"
              >
                <template #content>
                  <svg
                    class="ub__pm-svg"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    viewBox="0 0 24 24"
                  ><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line
                    x1="16"
                    y1="13"
                    x2="8"
                    y2="13"
                  /><line
                    x1="16"
                    y1="17"
                    x2="8"
                    y2="17"
                  /></svg>
                </template>
              </PaymentMethodRadio>

              <!-- Bank Transfer (admin) -->
              <PaymentMethodRadio
                v-if="showAdminPaymentOptions"
                v-model="paymentOption"
                :selected="paymentOption == paymentTypes.bankTransfer"
                :label="$t('checkout.bankTransferOrder')"
                :radio-value="paymentTypes.bankTransfer"
              >
                <template #content>
                  <svg
                    class="ub__pm-svg"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    viewBox="0 0 24 24"
                  ><path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" /></svg>
                </template>
              </PaymentMethodRadio>

              <!-- B2B (admin) -->
              <PaymentMethodRadio
                v-if="showAdminPaymentOptions"
                v-model="paymentOption"
                :selected="paymentOption == paymentTypes.b2b"
                :label="$t('checkout.b2b')"
                :radio-value="paymentTypes.b2b"
              >
                <template #content>
                  <svg
                    class="ub__pm-svg"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    viewBox="0 0 24 24"
                  ><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle
                    cx="9"
                    cy="7"
                    r="4"
                  /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                </template>
              </PaymentMethodRadio>

              <!-- PDQ (admin) -->
              <PaymentMethodRadio
                v-if="showAdminPaymentOptions"
                v-model="paymentOption"
                :selected="paymentOption == paymentTypes.pdq"
                :label="$t('checkout.pdq')"
                :radio-value="paymentTypes.pdq"
              >
                <template #content>
                  <svg
                    class="ub__pm-svg"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    viewBox="0 0 24 24"
                  ><rect
                    x="2"
                    y="5"
                    width="20"
                    height="14"
                    rx="2"
                  /><line
                    x1="2"
                    y1="10"
                    x2="22"
                    y2="10"
                  /></svg>
                </template>
              </PaymentMethodRadio>
            </v-radio-group>
          </div>
        </section>

        <!-- Payment form card -->
        <transition
          name="ub-fade"
          mode="out-in"
        >
          <section
            v-if="order.totalPrice != 0"
            :key="paymentOption"
            class="ub__card ub__card--form"
          >
            <buy-now-payment-form
              v-if="isBuyNowFlow"
              :order-id="buyNowOrderId"
              :locale="countryCode"
              @done="onBuyNowDone"
              @error="onBuyNowError"
            />
            <mobile-payment-form
              v-else-if="paymentOption == paymentTypes.mpesa || paymentOption == paymentTypes.mtn"
              :is-admin-order="isAdminOrder"
              :is-print-order="hasPrintBooks"
              container-classes=""
              :payment-types="paymentTypes"
              :payment-provider="paymentOption"
            />
            <cash
              v-else-if="isOfflinePayment"
              :is-print-order="hasPrintBooks"
              container-classes=""
              :payment-method="offlinePaymentMethod"
            />
            <B2bPayment
              v-else-if="paymentOption == paymentTypes.b2b"
              :is-print-order="hasPrintBooks"
              container-classes=""
            />
          </section>
        </transition>
      </div>

      <!-- ═══════ RIGHT COLUMN: Summary ═══════ -->
      <aside
        class="ub__side"
        data-testid="order-summary"
      >
        <div class="ub__sticky">
          <div class="ub__card">
            <!-- Collapsible cart summary -->
            <ekitabu-expansion-panel
              v-model="summaryPanel"
              accordion
              flat
              class="ub__summary-panels"
              panel-class="ub__summary-panel"
              header-class="ub__sum-toggle"
            >
              <template #header>
                <div class="ub__sum-toggle-left">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle
                      cx="9"
                      cy="21"
                      r="1"
                    /><circle
                      cx="20"
                      cy="21"
                      r="1"
                    />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                  <span class="ub__sum-title">
                    {{ $t('checkout.cartSummary') }}
                    <span class="ub__sum-count">{{ $tc('checkout.itemCount', order.orderLines.totalCount, { count: order.orderLines.totalCount }) }}</span>
                  </span>
                </div>
              </template>
              <ul
                class="ub__items"
                data-testid="checkout-items-list"
              >
                <li
                  v-for="({ node: line }, idx) in order.orderLines.edges"
                  :key="line.id || idx"
                  class="ub__item"
                  :class="{ 'ub__item--animate': shouldAnimate }"
                  :style="shouldAnimate ? { animationDelay: itemDelay(idx) + 'ms' } : undefined"
                  data-testid="checkout-item"
                >
                  <div class="ub__item-body">
                    <order-book-display-item
                      class="ub__item-book"
                      :order="order"
                      :order-line="line"
                      :show-quantity-input="false"
                    />
                    <div class="ub__item-chips">
                      <ekitabu-chip
                        class="ub__format-chip"
                        :color="getItemFormatColor(line.format)"
                        label
                        small
                        dark
                      >
                        <ekitabu-icon
                          aria-hidden="true"
                          class="mr-1"
                          small
                        >
                          {{ getItemFormatIcon(line.format) }}
                        </ekitabu-icon>
                        {{ getItemFormatLabel(line.format) }}
                      </ekitabu-chip>
                    </div>
                  </div>
                  <div class="ub__item-right">
                    <remove-from-cart-button
                      :id="line.id"
                      class="ub__item-remove"
                      data-testid="checkout-remove-item"
                      @onRemoved="onItemRemoved"
                    />
                    <div v-if="line.format === formats.print">
                      <order-line-quantity-input
                        :order-line-id="line.id"
                        :quantity="line.quantity"
                        small
                        @input="(qty) => optimisticQuantityUpdate({ orderLineId: line.id, quantity: qty })"
                        @quantityFailed="rollbackOptimisticUpdate"
                      />
                    </div>
                    <span
                      v-else-if="line.quantity > 1"
                      class="ub__item-qty"
                    >
                      {{ $t('checkout.qty', { count: line.quantity }) }}
                    </span>
                    <span v-else />
                  </div>
                </li>
              </ul>
            </ekitabu-expansion-panel>

            <!-- Promotion -->
            <div class="ub__section">
              <h3 class="ub__h3">
                {{ $t('checkout.promotion') }}
              </h3>
              <apply-coupon
                v-if="!order.orderCoupon"
                :existing-coupon-code="order.orderCoupon ? order.orderCoupon.code : null"
                :order-id="order.id"
                @cartUpdated="refreshOrder"
              />
              <div
                v-if="order.orderCoupon"
                class="ub__coupon-applied"
              >
                <span class="ub__coupon-tag">
                  {{ order.orderCoupon.code }}
                  <remove-coupon
                    :order-id="order.id"
                    @cartUpdated="refreshOrder"
                  />
                </span>
                <span
                  v-if="order.orderCoupon.description"
                  class="ub__coupon-desc"
                >
                  {{ order.orderCoupon.description }}
                </span>
              </div>
            </div>

            <!-- Order total -->
            <div
              class="ub__section"
              aria-live="polite"
              aria-atomic="true"
              data-testid="order-totals"
            >
              <h3 class="ub__h3">
                {{ $t('checkout.orderTotal') }}
              </h3>
              <div
                class="ub__row"
                data-testid="checkout-subtotal"
              >
                <span>{{ $t('checkout.subtotal') }}</span>
                <span class="ub__price-val">
                  <price
                    :book-price="Number(order.totalLineItemsPrice) || 0"
                    :base-currency="order.currency._id"
                    :show-free-text="false"
                  />
                </span>
              </div>
              <transition name="ub-row">
                <div
                  v-if="Number(order.shippingFee) > 0"
                  class="ub__row"
                >
                  <span>{{ $t('checkout.shipping') }}</span>
                  <span class="ub__price-val">
                    <price
                      :book-price="Number(order.shippingFee)"
                      :base-currency="order.currency._id"
                      :show-free-text="false"
                    />
                  </span>
                </div>
              </transition>
              <transition name="ub-row">
                <div
                  v-if="Number(order.totalTax) > 0"
                  class="ub__row"
                >
                  <span>{{ $t('checkout.vat') }}</span>
                  <span class="ub__price-val">
                    <price
                      :book-price="Number(order.totalTax) || 0"
                      :base-currency="order.currency._id"
                      :show-free-text="false"
                    />
                  </span>
                </div>
              </transition>
              <transition name="ub-row">
                <div
                  v-if="taxInclusiveDiscount > 0"
                  class="ub__row ub__row--discount"
                >
                  <span>{{ $t('checkout.discount') }}</span>
                  <span class="ub__price-val">
                    &minus;<price
                      :book-price="taxInclusiveDiscount"
                      :base-currency="order.currency._id"
                      :show-free-text="false"
                    />
                  </span>
                </div>
              </transition>
            </div>

            <!-- Final total -->
            <div
              class="ub__final"
              aria-live="polite"
              aria-atomic="true"
              data-testid="checkout-total"
            >
              <span>{{ $t('checkout.total') }}</span>
              <span class="ub__final-amount">
                <price
                  :book-price="displayTotalPrice"
                  :base-currency="order.currency._id"
                  :show-free-text="false"
                />
              </span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import currencyMixins from "@/mixins/currencyMixins";
import ApolloErrorAlert from "@/components/ApolloErrorAlert";
import NoCostOrder from "./NoCostOrder";
import AdminCheckMixin from "@/mixins/AdminCheckMixin";
import { FORMATS } from "@/utils/Const";
import optimisticCartUpdateMixin from "@/mixins/optimisticCartUpdateMixin";
import { getFormatColor, getFormatIcon, getFormat } from '@/utils/BookFormatHelper';
import gql from "graphql-tag";
import OrderFragments from "@/graphql/OrderFragments";
import Cash from "./Cash";
import PaymentMethodRadio from "./PaymentMethodRadio";
import MobilePaymentForm from "./MobilePaymentForm";
import featureFlags from "@/mixins/featureFlags";
import B2bPayment from "./B2bPayment";
import ApplyCoupon from "@/components/coupons/ApplyCoupon";
import RemoveCoupon from "@/components/coupons/RemoveCoupon";
import OrderBookDisplayItem from "@/components/OrderBookDisplayItem";
import RemoveFromCartButton from "./Cart/RemoveFromCartButton";
import OrderLineQuantityInput from "@/components/inputs/OrderLineQuantityInput";
import EmptyCartState from "@/components/EmptyCartState.vue";
import Price from "@/components/Price.vue";
import BuyNowPaymentForm from "./BuyNowPaymentForm";
import CheckoutBuyNowMixin from "@/mixins/CheckoutBuyNowMixin";
import { getOrderDiscountWithTax } from "@/utils/priceCalculator";
import { ROUND_TO_INT_CURRENCIES } from "@/utils/Const";
import { buildBookUrl } from "@/helpers/slugHelper";

const mpesaLogoPath = require("@/assets/img/mpesa_logo.png");
const mtnLogoPath = "https://shop-cdn.fra1.digitaloceanspaces.com/img/MoMo%20Logo-05.jpg";

const PAYMENT_TYPES = {
  mpesa: "mpesa",
  cashPayment: "cash",
  paybill: "paybill",
  bankTransfer: "bankTransfer",
  mtn: "mtn",
  b2b: "b2b",
  pdq: "pdq"
};

const ORDER_DETAILS_FRAGMENT = OrderFragments.orderDetailsFragment;

export default {
  name: "CheckoutScreen",
  metaInfo() {
    return {
      title: this.$t('routes.checkout'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.$t('seo.checkoutDescription'),
        },
      ],
    }
  },
  components: {
    ApolloErrorAlert,
    NoCostOrder,
    Cash,
    PaymentMethodRadio,
    MobilePaymentForm,
    B2bPayment,
    ApplyCoupon,
    RemoveCoupon,
    OrderBookDisplayItem,
    RemoveFromCartButton,
    OrderLineQuantityInput,
    EmptyCartState,
    Price,
    BuyNowPaymentForm,
  },
  mixins: [
    currencyMixins,
    AdminCheckMixin,
    featureFlags,
    optimisticCartUpdateMixin,
    CheckoutBuyNowMixin,
  ],
  data() {
    return {
      paymentOption: PAYMENT_TYPES.mpesa,
      isAdminOrder: false,
      summaryPanel: 0,
      fetching: false,
      fetchError: null,
      shouldAnimate: true,
    };
  },
  computed: {
    mpesaLogo() {
      return mpesaLogoPath;
    },
    mtnLogo() {
      return mtnLogoPath;
    },
    showAdminPaymentOptions() {
      return !this.isBuyNowFlow && this.isAdminOrder;
    },
    countryCode() {
      return this.$store.state.locale.countryCode;
    },
    orderId() {
      return this.isBuyNowFlow ? this.buyNowOrderId : this.$store.state.cart.currentCart;
    },
    order() {
      return this.isBuyNowFlow ? this.buyNowOrderData : this.$store.state.cart.cartData;
    },
    initialLoading() {
      return this.fetching && !this.order;
    },
    getOrderQuery() {
      return gql`
        query GetOrder($orderId: ID!) {
          order(id: $orderId) {
            ...OrderDetails
          }
        }
        ${ORDER_DETAILS_FRAGMENT}
      `;
    },
    paymentTypes() {
      return PAYMENT_TYPES;
    },
    isOfflinePayment() {
      return [
        PAYMENT_TYPES.cashPayment,
        PAYMENT_TYPES.paybill,
        PAYMENT_TYPES.bankTransfer,
        PAYMENT_TYPES.pdq
      ].includes(this.paymentOption);
    },
    offlinePaymentMethod() {
      const methodMap = {
        [PAYMENT_TYPES.cashPayment]: 'cash',
        [PAYMENT_TYPES.paybill]: 'paybill',
        [PAYMENT_TYPES.bankTransfer]: 'bank_transfer',
        [PAYMENT_TYPES.pdq]: 'pdq',
      };
      return methodMap[this.paymentOption] || 'cash';
    },
    formats() {
      return FORMATS;
    },
    hasPrintBooks() {
      return !!this.order?.orderLines?.edges?.find(
        (e) => e.node.format === FORMATS.print
      );
    },
    /**
     * Discount amount inclusive of tax, consistent with other tax-inclusive
     * totals shown in the checkout summary.
     */
    taxInclusiveDiscount() {
      return getOrderDiscountWithTax(this.order);
    },
    displayTotalPrice() {
      const totalPrice = Number(this.order?.totalPrice || 0);
      const currencyCode = this.order?.currency?._id;

      if (ROUND_TO_INT_CURRENCIES.includes(currencyCode)) {
        return Math.round(totalPrice);
      }

      return Number(totalPrice.toFixed(2));
    },
  },
  watch: {
    isAdminOrder() {
      if (!this.isAdminOrder) {
        this.paymentOption = PAYMENT_TYPES.mpesa;
      }
    },
    'order.confirmed'(confirmed) {
      if (confirmed && !this.isBuyNowFlow) {
        this.$store.commit('setCurrentCart', null);
      }
    },
    order(newOrder) {
      if (newOrder && this.shouldAnimate) {
        this.disableAnimationAfterEntrance();
      }
    },
  },
  created() {
    if (this.isBuyNowFlow) {
      if (!this.buyNowOrderId) {
        this.$router.push({ name: "home" });
      }
    } else if (!this.orderId) {
      this.$router.push({ name: "home" });
    }
  },
  mounted() {
    if (this.isBuyNowFlow && this.buyNowOrderId) {
      this.fetchBuyNowOrder();
    } else if (this.orderId) {
      this.fetchCart();
    }
    this.disableAnimationAfterEntrance();
  },
  methods: {
    /** Stagger delay per item, capped so large carts don't wait too long. */
    itemDelay(index) {
      const MAX_STAGGER_ITEMS = 8;
      const DELAY_PER_ITEM_MS = 50;
      return Math.min(index, MAX_STAGGER_ITEMS) * DELAY_PER_ITEM_MS;
    },
    /**
     * Wait for the last animated item to finish its entrance animation,
     * then disable the animation flag so data updates don't replay it.
     */
    disableAnimationAfterEntrance() {
      if (!this.shouldAnimate) return;
      this.$nextTick(() => {
        const items = this.$el.querySelectorAll('.ub__item--animate');
        const lastItem = items[items.length - 1];
        if (lastItem) {
          lastItem.addEventListener('animationend', () => {
            this.shouldAnimate = false;
          }, { once: true });
        }
      });
    },
    getItemFormatColor(format) {
      return getFormatColor(format);
    },
    getItemFormatIcon(format) {
      return getFormatIcon(format);
    },
    getItemFormatLabel(format) {
      return getFormat(format || '');
    },
    refreshOrder() {
      if (this.isBuyNowFlow) {
        this.fetchBuyNowOrder();
      } else {
        this.fetchCart();
      }
    },
    async fetchCart() {
      if (!this.orderId) return;
      this.fetching = true;
      this.fetchError = null;
      try {
        const result = await this.$apollo.query({
          query: this.getOrderQuery,
          variables: { orderId: this.orderId },
          fetchPolicy: 'network-only',
        });
        const order = result.data?.order;
        if (!order) {
          this.$store.commit('setCurrentCart', null);
        } else {
          this.$store.commit('setCartData', order);
        }
      } catch (e) {
        if (!this.order) this.fetchError = e;
      } finally {
        this.fetching = false;
      }
    },
    rollbackOptimisticUpdate() {
      if (this.isBuyNowFlow) {
        this.fetchBuyNowOrder();
      } else {
        this.fetchCart();
      }
    },
    async onItemRemoved() {
      if (this.isBuyNowFlow) {
        if (this.order?.orderLines?.totalCount <= 1) {
          this.returnToBuyNowSource();
          return;
        }

        await this.fetchBuyNowOrder();
        if (!this.buyNowOrderData || !this.buyNowOrderData.orderLines?.totalCount) {
          this.returnToBuyNowSource();
        }
      } else {
        await this.fetchCart();
        if (!this.order || !this.order.orderLines?.totalCount) {
          this.$store.commit('setCartData', null);
          this.$router.push({ name: 'cart' });
        }
      }
    },
    returnToBuyNowSource() {
      const buyNowBook = this.$store.state.cart.buyNowBook;
      const target = buyNowBook?.returnPath || (buyNowBook?.id ? buildBookUrl(buyNowBook) : buyNowBook?.bookRoute || { name: 'home' });
      this.$store.commit('clearBuyNow');
      this.$router.push(target).catch((err) => {
        if (err.name !== 'NavigationDuplicated') throw err;
      });
    },
  },
};
</script>

<style scoped lang="scss">
/* ═══════════════════════════════════════════════════
   Checkout — Uber Eats inspired
   Two-column, white cards on gray, bold headings,
   bordered payment cards, clean typography
   ═══════════════════════════════════════════════════ */

/* ── Page ── */
.ub {
  min-height: 100vh;
  background: $book-grey-lightest;
  font-family: $book-font-body;
  color: $book-ink;
  overflow-x: hidden;
}

/* ── Grid shell ── */
.ub__shell {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 24px;
  max-width: 1080px;
  margin: 0 auto;
  padding: 24px 24px 60px;
  align-items: start;
}

/* ── Card ── */
.ub__card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba($book-ink, 0.06);
  overflow: hidden;
}

.ub__card--form {
  margin-top: 16px;
  padding: 24px;
}

/* ── Skeleton cards ── */
.ub__skel {
  background: white;
  border-radius: 16px;
  margin-bottom: 16px;
}

/* ── Card head ── */
.ub__card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 20px;
}

/* ── Headings ── */
.ub__h2 {
  font-family: $book-font-body;
  font-size: 1.375rem;
  font-weight: 800;
  color: $book-ink;
  margin: 0;
  letter-spacing: -0.01em;
}

.ub__h3 {
  font-family: $book-font-body;
  font-size: 1rem;
  font-weight: 800;
  color: $book-ink;
  margin: 0 0 12px;
}

/* ── Pill toggle (Standard / Admin) ── */
.ub__pill-toggle {
  display: inline-flex;
  background: $book-grey-lightest;
  border-radius: 500px;
  padding: 4px;
}

.ub__pill {
  padding: 8px 18px;
  font-family: $book-font-body;
  font-size: 0.8125rem;
  font-weight: 700;
  border-radius: 500px;
  border: none;
  background: transparent;
  color: $book-grey;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;
}

.ub__pill--on {
  background: $book-ink;
  color: white;
}

/* ── Payment methods area ── */
.ub__methods {
  padding: 0 24px 24px;
}

/* ── Zero-cost form area ── */
.ub__form-area {
  padding: 0 24px 24px;
}

/* ── Radio group reset ── */
.ub__rg {
  margin: 0 !important;
  padding: 0 !important;
}

.ub__rg ::v-deep .v-input__control {
  width: 100%;
}

.ub__rg ::v-deep .v-input__slot {
  margin-bottom: 0;
}

/* ── Payment SVG icons ── */
.ub__methods ::v-deep .ub__pm-svg {
  color: $book-grey;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.ub__methods ::v-deep .ub-pm--on .ub__pm-svg {
  color: $book-ink;
}

/* ═══════════════════════════════════════
   Form card overrides
   ═══════════════════════════════════════ */

/* Labels */
::v-deep .ub__card--form .input-label {
  font-family: $book-font-body;
  font-size: 0.8125rem;
  font-weight: 600;
  color: $book-ink;
  padding-bottom: 6px;
}

/* Input fields */
::v-deep .ub__card--form .input-field {
  border: 1px solid $book-grey-lighter !important;
  border-radius: 12px !important;
  background: white !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

::v-deep .ub__card--form .input-field:focus-within {
  border-color: $book-ink !important;
  box-shadow: 0 0 0 3px rgba($book-ink, 0.06) !important;
}

::v-deep .ub__card--form .input-field .v-input__slot {
  min-height: 48px !important;
  height: 48px;
  border-radius: 12px !important;
  padding: 0 14px !important;
  box-shadow: none !important;
}

::v-deep .ub__card--form .input-field input {
  font-family: $book-font-body;
  font-size: 0.9375rem;
  color: $book-ink;

  &::placeholder {
    color: $book-grey-light !important;
    font-weight: 400 !important;
  }
}

/* Select fields */
::v-deep .ub__card--form .input-select {
  border: 1px solid $book-grey-lighter !important;
  border-radius: 12px !important;
}

::v-deep .ub__card--form .input-select:focus-within {
  border-color: $book-ink !important;
  box-shadow: 0 0 0 3px rgba($book-ink, 0.06) !important;
}

/* Phone input */
::v-deep .ub__card--form .vue-tel-input {
  border: 1px solid $book-grey-lighter !important;
  border-radius: 12px !important;
  background: white !important;
  min-height: 48px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: $book-ink !important;
    box-shadow: 0 0 0 3px rgba($book-ink, 0.06) !important;
  }
}

/* Error text */
::v-deep .ub__card--form .error-text {
  font-size: 0.75rem;
  color: $book-red-danger;
  margin-top: 4px;
}

/* Alert */
::v-deep .ub__card--form .v-alert {
  border-radius: 12px !important;
  font-family: $book-font-body;
  font-size: 0.8125rem;
}

/* Checkbox labels */
::v-deep .ub__card--form .v-input--checkbox .v-label {
  font-family: $book-font-body;
  font-size: 0.875rem;
  color: $book-ink;
}

/* Radio labels (delivery options) */
::v-deep .ub__card--form .v-radio .v-label {
  font-family: $book-font-body;
  font-size: 0.9375rem;
  font-weight: 500;
  color: $book-ink;
}

/* Typography inside form */
::v-deep .ub__card--form h4 {
  font-family: $book-font-body;
  font-size: 0.875rem;
  font-weight: 700;
  color: $book-grey-dark;
  margin-top: 16px;
  margin-bottom: 4px;
}

::v-deep .ub__card--form h5 {
  font-family: $book-font-body;
  font-size: 0.75rem;
  font-weight: 500;
  color: $book-grey-light;
  margin-top: 4px;
}

::v-deep .ub__card--form p {
  font-family: $book-font-body;
  font-size: 0.9375rem;
  color: $book-ink;
  margin-bottom: 0;
}

/* Place Order button */
::v-deep .ub__form-area .primary-button,
::v-deep .ub__card--form .primary-button {
  width: 100%;
  min-height: 44px !important;
  height: 44px !important;
  border-radius: 10px !important;
  font-family: $book-font-body !important;
  font-size: 0.875rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.01em !important;
  text-transform: none !important;
  margin-top: 12px;
  box-shadow: none !important;
  transition: opacity 0.15s ease !important;
  background-image: $book-brand-gradient !important;

  &:hover:not(:disabled) {
    opacity: 0.88;
  }
}

/* ═══════════════════════════════════════
   Right sidebar
   ═══════════════════════════════════════ */

.ub__sticky {
  position: sticky;
  top: 20px;
}

/* ── Vuetify expansion panel overrides for cart summary ── */
/* The cart summary now renders inside EkitabuExpansionPanel. Elements the
   wrapper renders (the panel + header it puts panel-class/header-class on)
   detach from this scope and need ::v-deep. Header/content markup passed via
   slots keeps this component's scope, so those classes (.ub__sum-toggle-left,
   .ub__sum-title, .ub__items, ...) stay plain. */
.ub__summary-panels {
  box-shadow: none !important;
}

::v-deep .ub__summary-panel {
  background: transparent !important;
}

::v-deep .ub__summary-panel::before {
  box-shadow: none !important;
}

::v-deep .ub__summary-panel .v-expansion-panel-content__wrap {
  padding: 0;
}

::v-deep .ub__sum-toggle {
  padding: 20px 24px !important;
  min-height: unset !important;
  border-bottom: 1px solid $book-grey-lightest;

  &:hover {
    background: $book-hover-bg;
  }

  &:focus-visible {
    outline: 2px solid $book-ink;
    outline-offset: -2px;
  }
}

.ub__sum-toggle-left {
  display: flex;
  align-items: center;
  gap: 10px;
  color: $book-ink;
}

.ub__sum-title {
  font-family: $book-font-body;
  font-size: 0.9375rem;
  font-weight: 700;
  color: $book-ink;
}

.ub__sum-count {
  font-weight: 500;
  color: $book-grey;
}

/* ── Items list ── */
.ub__items {
  list-style: none;
  margin: 0;
  padding: 8px 24px 16px;
  border-bottom: 1px solid $book-grey-lightest;
}

.ub__item {
  display: flex;
  align-items: stretch;
  gap: 12px;
  padding: 12px 0;
}

.ub__item + .ub__item {
  border-top: 1px solid $book-grey-lightest;
}

/* Body: flex-column holding OrderBookDisplayItem + chips row */
.ub__item-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* OrderBookDisplayItem — show full content (cover + title + author + price) */
::v-deep .ub__item-book .book-cover-image {
  width: 40px !important;
  height: 56px !important;
  border-radius: 4px;
  overflow: hidden;
}

::v-deep .ub__item-book h3.subtitle-2 {
  font-family: $book-font-body;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.3;
  color: $book-ink;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

::v-deep .ub__item-book h3.subtitle-2.font-weight-regular,
::v-deep .ub__item-book h4 {
  font-family: $book-font-body;
  font-size: 0.6875rem;
  font-weight: 500;
  color: $book-grey;
  line-height: 1.3;
}

::v-deep .ub__item-book .gradient-text {
  font-family: $book-font-body;
  font-size: 0.8125rem;
  font-weight: 700;
  background: none;
  -webkit-background-clip: unset;
  -webkit-text-fill-color: $book-ink;
  color: $book-ink;
}

::v-deep .ub__item-book .discount-text,
::v-deep .ub__item-book .discount-text.gradient-text {
  -webkit-text-fill-color: $book-green;
  color: $book-green;
}

::v-deep .ub__item-book .striked-text.gradient-text {
  -webkit-text-fill-color: $book-grey;
  color: $book-grey;
}

/* Format chip row — indented to align with text after cover */
.ub__item-chips {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding-left: 52px;
}

.ub__format-chip {
  border-radius: 4px !important;
  font-family: $book-font-body;
  font-size: 0.6875rem;
  font-weight: 600;
  height: 22px !important;
}

/* Right column: delete at top, qty at bottom */
.ub__item-right {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
}

.ub__item-qty {
  font-size: 0.6875rem;
  font-weight: 600;
  color: $book-grey;
}

/* Remove button overrides */
::v-deep .ub__item-remove .v-btn--icon {
  width: 26px !important;
  height: 26px !important;
  opacity: 0.4;
  transition: opacity 0.15s ease;
}

.ub__item:hover ::v-deep .ub__item-remove .v-btn--icon {
  opacity: 1;
}

::v-deep .ub__item-remove .v-btn--icon .v-icon {
  font-size: 14px !important;
  color: $book-grey-light !important;
  transition: color 0.15s ease;
}

.ub__item:hover ::v-deep .ub__item-remove .v-btn--icon .v-icon {
  color: $book-red-danger !important;
}

/* Quantity stepper overrides inside sidebar */
::v-deep .ub__items .qty-stepper {
  height: 26px;
  border-radius: 6px;
}

::v-deep .ub__items .qty-stepper__btn {
  width: 26px;
  height: 26px;
}

::v-deep .ub__items .qty-stepper__value {
  width: 28px;
  height: 26px;
  font-size: 0.75rem;
}

/* ── Section dividers ── */
.ub__section {
  padding: 16px 24px;
  border-bottom: 1px solid $book-grey-lightest;
}

/* ── Coupon applied ── */
.ub__coupon-applied {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
}

.ub__coupon-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba($book-green, 0.08);
  border: 1px solid rgba($book-green, 0.2);
  border-radius: 8px;
  padding: 8px 10px 8px 12px;
  color: $book-green;
  font-family: $book-font-body;
  font-size: 0.8125rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  align-self: flex-start;
}

.ub__coupon-desc {
  font-family: $book-font-body;
  font-size: 0.75rem;
  color: $book-grey;
  line-height: 1.3;
}

/* ── Total rows ── */
.ub__row {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  font-weight: 500;
  color: $book-ink;
  padding: 3px 0;
}

.ub__row--discount {
  color: $book-green;
}

/* ── Final total ── */
.ub__final {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 16px 24px 24px;
  font-size: 1rem;
  font-weight: 800;
  color: $book-ink;
}

.ub__final-amount {
  font-size: 1.25rem;
  font-weight: 800;
}

.ub__original-price {
  text-decoration: line-through;
  color: $book-grey;
  font-size: 0.85rem;
  font-weight: 500;
  margin-right: 6px;
}

::v-deep .ub__original-price .gradient-text {
  -webkit-text-fill-color: $book-grey !important;
  color: $book-grey !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
}

/* Override Price component gradient-text in order totals */
::v-deep .ub__row .gradient-text,
::v-deep .ub__final .gradient-text {
  background: none !important;
  -webkit-background-clip: unset !important;
  -webkit-text-fill-color: inherit !important;
  color: inherit !important;
  font-family: inherit !important;
  font-size: inherit !important;
  font-weight: inherit !important;
}

/* ═══════ Entrance animation (plays once via v-once-like CSS) ═══════ */
.ub__main > * {
  animation: ub-up 0.35s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

.ub__main > *:nth-child(2) {
  animation-delay: 40ms;
}

.ub__main > *:nth-child(3) {
  animation-delay: 80ms;
}

.ub__side {
  animation: ub-up 0.35s cubic-bezier(0.16, 1, 0.3, 1) backwards;
  animation-delay: 60ms;
}

@keyframes ub-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ═══════ Item stagger animation (play once) ═══════ */
.ub__item--animate {
  animation: ub-item-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

@keyframes ub-item-in {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ═══════ Transition ═══════ */
.ub-fade-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.ub-fade-leave-active {
  transition: opacity 0.12s ease;
}

.ub-fade-enter {
  opacity: 0;
  transform: translateY(8px);
}

.ub-fade-leave-to {
  opacity: 0;
}

/* Collapse transition handled by Vuetify v-expansion-panel */

/* ═══════ Row appear/disappear (discount, tax, shipping) ═══════ */
.ub-row-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease, max-height 0.3s ease;
  max-height: 40px;
  overflow: hidden;
}

.ub-row-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease, max-height 0.2s ease;
  overflow: hidden;
}

.ub-row-enter {
  opacity: 0;
  transform: translateY(-6px);
  max-height: 0;
}

.ub-row-leave-to {
  opacity: 0;
  transform: translateY(6px);
  max-height: 0;
}

/* ═══════ Responsive ═══════ */
@media (max-width: 899px) {
  .ub__shell {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px 16px 40px;
  }

  .ub__side {
    order: -1;
  }

  .ub__sticky {
    position: static;
  }

  .ub__card-head {
    padding: 20px 20px 16px;
    flex-wrap: wrap;
    gap: 12px;
  }

  .ub__methods {
    padding: 0 20px 20px;
  }

  .ub__card--form {
    padding: 20px;
  }

  ::v-deep .ub__sum-toggle {
    padding: 16px 20px;
    min-height: 48px;
  }

  .ub__items {
    padding: 8px 20px 12px;
  }

  .ub__section {
    padding: 12px 20px;
  }

  .ub__final {
    padding: 12px 20px 20px;
  }

  .ub__form-area {
    padding: 0 20px 20px;
  }

  /* Touch devices: always show remove button, ensure 44px touch target */
  ::v-deep .ub__item-remove .v-btn--icon {
    opacity: 1;
    width: 44px !important;
    height: 44px !important;
    margin: -9px -9px 0 0;
  }

  ::v-deep .ub__item-remove .v-btn--icon .v-icon {
    font-size: 16px !important;
  }

  /* Ensure quantity stepper buttons meet touch target */
  ::v-deep .ub__items .qty-stepper__btn {
    width: 36px;
    height: 36px;
  }

  ::v-deep .ub__items .qty-stepper {
    height: 36px;
  }

  ::v-deep .ub__items .qty-stepper__value {
    height: 36px;
  }

  /* Match cart drawer item spacing on tablet */
  .ub__item {
    gap: 10px;
    padding: 10px 0;
  }

  .ub__item-chips {
    padding-left: 52px;
    flex-wrap: wrap;
  }

  /* Reduce cover size to match cart drawer */
  ::v-deep .ub__item-book .book-cover-image {
    width: 36px !important;
    height: 50px !important;
  }

  ::v-deep .ub__item-book .mr-3 {
    margin-right: 8px !important;
  }

  /* Tighter text on mobile */
  ::v-deep .ub__item-book h3.subtitle-2 {
    font-size: 0.75rem;
  }

  ::v-deep .ub__item-book h3.subtitle-2.font-weight-regular,
  ::v-deep .ub__item-book h4 {
    font-size: 0.625rem;
  }

  .ub__row {
    font-size: 0.8125rem;
  }

  .ub__final-amount {
    font-size: 1.125rem;
  }
}

@media (max-width: 599px) {
  .ub__shell {
    padding: 12px 12px 32px;
    gap: 12px;
  }

  .ub__h2 {
    font-size: 1.25rem;
  }

  .ub__card-head {
    padding: 18px 16px 14px;
  }

  .ub__methods {
    padding: 0 16px 16px;
  }

  .ub__card--form {
    padding: 16px;
  }

  ::v-deep .ub__sum-toggle {
    padding: 14px 16px;
    min-height: 48px;
  }

  .ub__items {
    padding: 6px 16px 10px;
  }

  .ub__item {
    gap: 10px;
    padding: 10px 0;
  }

  .ub__section {
    padding: 10px 16px;
  }

  .ub__final {
    padding: 10px 16px 18px;
  }

  .ub__form-area {
    padding: 0 16px 16px;
  }

  .ub__pill {
    padding: 6px 14px;
    font-size: 0.75rem;
  }

  /* Align chip row with text after smaller cover (32px + 8px gap) */
  .ub__item-chips {
    padding-left: 40px;
    flex-wrap: wrap;
  }

  ::v-deep .ub__item-book .book-cover-image {
    width: 32px !important;
    height: 44px !important;
  }

  ::v-deep .ub__item-book .mr-3 {
    margin-right: 6px !important;
  }

  /* Prevent long book titles from overflowing */
  ::v-deep .ub__item-book h3.subtitle-2 {
    font-size: 0.6875rem;
    -webkit-line-clamp: 2;
    word-break: break-word;
  }

  ::v-deep .ub__item-book h3.subtitle-2.font-weight-regular,
  ::v-deep .ub__item-book h4 {
    font-size: 0.5625rem;
  }

  .ub__item-qty {
    font-size: 0.625rem;
  }

  .ub__row {
    font-size: 0.75rem;
  }

  .ub__final-amount {
    font-size: 1rem;
  }

  .ub__coupon-tag {
    padding: 6px 8px 6px 10px;
    font-size: 0.6875rem;
  }

  ::v-deep .ub__card--form .input-field input,
  ::v-deep .ub__card--form .vue-tel-input input {
    font-size: 1rem !important;
  }
}
</style>
